import { Link } from "react-router-dom";
import { CalendarClock, Layers } from "lucide-react";
import type { ProductDto } from "@gaole/shared";
import { rm } from "../lib/format";
import { DEFAULT_COIN_ICON } from "../lib/credits";
import { Badge } from "./ui";
import { HoloCard } from "./fx/HoloCard";
import { ListingTitle } from "./ListingTitle";
import { useListingImpression } from "../hooks/useListingImpression";
import { WishlistHeart } from "./wishlist/WishlistHeart";
import { StarRating } from "./reviews/StarRating";
import { useAuth } from "../stores/auth";
import { isFlexxiShopProduct } from "../lib/flexxiShop";

export function productImageSrc(product: Pick<ProductDto, "displayImages" | "images">): string | null {
  return product.displayImages[0] ?? product.images[0] ?? null;
}

export function ProductPrice({
  price,
  compareAtPrice,
  size = "sm",
  showSavings = true,
}: {
  price: number;
  compareAtPrice?: number | null;
  size?: "sm" | "lg";
  showSavings?: boolean;
}) {
  const onSale = compareAtPrice != null && compareAtPrice > price;
  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      {onSale && (
        <span
          className={
            size === "lg" ? "text-sm text-white/40 line-through" : "text-[10px] text-white/40 line-through"
          }
        >
          {rm(compareAtPrice)}
        </span>
      )}
      <span
        className={
          size === "lg"
            ? "font-display text-2xl font-bold text-brand-pink"
            : "font-display text-sm font-semibold text-brand-pink"
        }
      >
        {rm(price)}
      </span>
      {onSale && showSavings && (
        <span className="rounded bg-emerald-500/15 px-1 py-0.5 text-[9px] font-medium text-emerald-400">
          Save {Math.round(((compareAtPrice - price) / compareAtPrice) * 100)}%
        </span>
      )}
    </div>
  );
}

export function ProductImage({
  src,
  alt,
  className = "",
  fit = "contain",
  backgroundColor,
}: {
  src: string | null;
  alt: string;
  className?: string;
  /** Thumbnails default to contain so the full product is visible; use cover for edge-to-edge fills. */
  fit?: "cover" | "contain";
  /** Hex background behind the image; defaults to theme surface when unset. */
  backgroundColor?: string | null;
}) {
  const objectFit = fit === "cover" ? "object-cover" : "object-contain";
  const bgStyle = backgroundColor ? { backgroundColor } : undefined;
  const bgClass = backgroundColor ? "" : "bg-surface-2";

  if (!src) {
    return (
      <div
        className={`flex max-w-full items-center justify-center text-white/30 ${bgClass} ${className}`}
        style={bgStyle}
      >
        <Layers size={32} strokeWidth={1.25} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`max-w-full ${bgClass} ${objectFit} ${className}`}
      style={bgStyle}
    />
  );
}

/** Scrim presets for hero images with badges or captions on top. */
export type HeroImageOverlay = false | "caption" | "badge" | "full";

export function HeroImageScrim({ overlay }: { overlay: Exclude<HeroImageOverlay, false> }) {
  return (
    <>
      {(overlay === "badge" || overlay === "full") && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-black/70 via-black/25 to-transparent"
        />
      )}
      {(overlay === "caption" || overlay === "full") && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-black/95 via-black/65 to-black/20"
        />
      )}
    </>
  );
}

/** Classes for text rendered over hero imagery — use with z-30 on the text container. */
export const heroCaptionTextClass =
  "drop-shadow-[0_2px_10px_rgba(0,0,0,0.95)] [text-shadow:0_1px_2px_rgba(0,0,0,0.85)]";

/** Full-width hero banner — contained foreground image with blurred cover fill behind. */
export function ProductHeroImage({
  src,
  alt = "",
  aspectClassName = "aspect-video",
  imageClassName = "",
  blurClassName = "scale-105 blur-md brightness-[0.45] saturate-110",
  overlay = false,
}: {
  src: string | null;
  alt?: string;
  aspectClassName?: string;
  imageClassName?: string;
  blurClassName?: string;
  /** Darkens the image so overlaid badges and captions stay readable. */
  overlay?: HeroImageOverlay;
}) {
  const scrim = overlay;

  return (
    <div className={`relative w-full min-w-0 max-w-full overflow-hidden bg-surface-2 ${aspectClassName}`}>
      {src ? (
        <>
          <img
            src={src}
            alt=""
            aria-hidden
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover ${blurClassName}`}
          />
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className={`relative z-10 mx-auto h-full w-full object-contain ${imageClassName}`}
          />
          {scrim && <HeroImageScrim overlay={scrim} />}
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/30">
          <Layers size={32} strokeWidth={1.25} />
        </div>
      )}
    </div>
  );
}

export function ProductCard({ product }: { product: ProductDto }) {
  const impressionRef = useListingImpression(product.id);
  const { config, moduleEnabled } = useAuth();
  const isPack = product.behavior === "PACK" || product.type === "PACK";
  const soldOut = product.stockAvailable <= 0 && product.type !== "CREDIT_PACK";
  const showInstalmentChip =
    !soldOut && Boolean(config?.flexxiEnabled) && moduleEnabled("instalments") && isFlexxiShopProduct(product);
  const isCreditPack = product.type === "CREDIT_PACK";
  const imageSrc = isCreditPack
    ? productImageSrc(product) ?? DEFAULT_COIN_ICON
    : productImageSrc(product);
  const imageBlock = (
    <HoloCard fill holo={0.22} max={7} lift={6} scale={1.05} className="rounded-t-2xl">
      <ProductImage
        src={imageSrc}
        alt={product.name}
        backgroundColor={product.imageBackgroundColor}
        className={`h-full w-full ${isCreditPack ? "p-4" : ""}`}
      />
    </HoloCard>
  );

  return (
    <Link
      ref={impressionRef}
      to={`/shop/${product.slug}`}
      className="group neon-ring block min-w-0 overflow-hidden rounded-2xl bg-surface transition-transform active:scale-[0.98] lg:hover:-translate-y-1 lg:hover:glow-brand"
    >
      <div className="relative aspect-square">
        <div className="block h-full w-full">{imageBlock}</div>
        {!isCreditPack && (
          <div className="absolute right-2 top-2 z-10">
            <WishlistHeart kind="SHOP" productId={product.id} />
          </div>
        )}
        {isPack && !soldOut && (
          <div className="absolute left-2 top-2">
            <Badge color="pink">Mystery pack</Badge>
          </div>
        )}
        {product.type === "BUNDLE" && (
          <div className="absolute left-2 top-2">
            <Badge color="blue">Bundle</Badge>
          </div>
        )}
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="font-display text-xs font-semibold uppercase tracking-widest text-white/80">
              Sold out
            </span>
          </div>
        )}
      </div>
      <div className="min-w-0 space-y-1 p-3">
        <ListingTitle>{product.name}</ListingTitle>
        {showInstalmentChip ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-pink/25 to-brand-blue/25 px-2 py-0.5 text-[10px] font-semibold text-white">
            <CalendarClock size={10} /> Instalment available
          </span>
        ) : null}
        {config?.shopReviewsEnabled && (product.ratingCount ?? 0) > 0 && (
          <div className="flex items-center gap-1">
            <StarRating value={Math.round(product.avgRating ?? 0)} readOnly size={12} />
            <span className="text-[10px] text-white/45">({product.ratingCount})</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <ProductPrice price={product.price} compareAtPrice={product.compareAtPrice} />
          {!soldOut && product.type !== "CREDIT_PACK" && (
            <span className="text-[10px] text-amber-300">
              {isPack ? `${product.pack?.totalRemaining ?? product.stockAvailable} in pool` : product.stockAvailable <= 3 ? `${product.stockAvailable} left` : null}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
